// src/pages/Webhooks.jsx
//
// Webhook configuration + simulator. Visual language matches the rest of
// the RF-style dashboard: rounded-md cards with a calm sky border, h-12
// card-header strips, uppercase eyebrow section titles, dashboard-standard
// inputs (`bg-[#000414] border-sky-900/40`), and small lucide IconBtns.
//
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Copy,
  Link2,
  Shield,
  RefreshCw,
  Settings2,
  Send,
  CheckCircle2,
  AlertTriangle,
  X,
  Webhook,
  Eye,
  EyeOff,
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";

const HANDLER_URL =
  "https://whiteintel.io/api/regular/app/v2/webhook/get_webhook_handler.php";
const LICENSE_URL =
  "https://whiteintel.io/api/regular/app/v2/organizations/get_license.php";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

/* ───────────────── Atoms (RF style) ───────────────── */

function SurfaceCard({ children, className = "", noPad = false }) {
  return (
    <div
      className={`rounded-md border border-sky-900/30 bg-[#0a1224]/70 overflow-hidden ${noPad ? "" : "p-5"} ${className}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ icon: Icon, title, right }) {
  return (
    <div className="px-4 h-12 shrink-0 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-sky-300/80" />}
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-300 truncate">
          {title}
        </h3>
      </div>
      <div className="shrink-0 whitespace-nowrap">{right}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-3 text-[13px] font-semibold tracking-wide text-neutral-400 uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[10.5px] uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-0.5 text-neutral-100 break-all">{value}</dd>
    </div>
  );
}

function IconBtn({ children, title, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded text-neutral-300 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

function SkeletonBar({ className = "" }) {
  return <div className={`bg-sky-900/30 rounded animate-pulse ${className}`} />;
}

/* Dismissible alert banner — same surface vocabulary as PaymentFraud's
   error / success banners. */
function AlertBanner({ kind = "success", children, onClose }) {
  const isSuccess = kind === "success";
  const ring = isSuccess
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
    : "border-red-500/30 bg-red-500/10 text-red-200";
  const Icon = isSuccess ? CheckCircle2 : AlertTriangle;
  return (
    <div
      role="alert"
      className={`rounded-md border ${ring} px-3 py-2 text-[12.5px] flex items-start justify-between gap-3`}
    >
      <span className="inline-flex items-start gap-2 min-w-0">
        <Icon className="h-3.5 w-3.5 mt-[2px] shrink-0" />
        <span className="leading-relaxed">{children}</span>
      </span>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-neutral-400 hover:text-white shrink-0"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

/* Inline copy chip — mono value + a small clipboard IconBtn that flashes a
   check on success. Used for endpoint URL + secret. */
function CopyInline({ children, copyText, className = "" }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <code className="font-mono break-all text-neutral-100">{children}</code>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(copyText ?? String(children ?? ""));
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {}
        }}
        title={copied ? "Copied" : "Copy"}
        className="rounded p-1 text-neutral-400 hover:text-sky-200 hover:bg-sky-500/10"
      >
        {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

/* ───────────────── Configure Modal ───────────────── */

function ConfigureWebhookModal({ open, onClose, initialEndpoint, onSave, saving }) {
  const [endpoint, setEndpoint] = useState(initialEndpoint || "");

  useEffect(() => {
    if (open) setEndpoint(initialEndpoint || "");
  }, [open, initialEndpoint]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-md bg-[#0a1224] border border-sky-900/40 p-5 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-sky-300/80">
              Webhook
            </div>
            <h3 className="mt-1 text-[15px] font-semibold tracking-tight text-neutral-100">
              Configure endpoint
            </h3>
          </div>
          <IconBtn title="Close" onClick={onClose}>
            <X className="h-4 w-4" />
          </IconBtn>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider mb-1.5 text-neutral-500">
              Endpoint URL
            </div>
            <input
              type="url"
              placeholder="https://example.com/webhooks/whiteintel"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50"
            />
            <div className="mt-1 text-[11px] text-neutral-500">
              HTTPS endpoint that will receive POST requests from WhiteIntel.
            </div>
          </div>

          <div className="rounded-md border border-sky-900/30 bg-[#0b1426]/60 px-3 py-2 text-[11.5px] text-neutral-400">
            We sign each request with your secret. Rotate it if you suspect exposure.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-sky-900/30">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-md border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ endpoint })}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 disabled:opacity-50 disabled:hover:bg-sky-500/15"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Confirm Rotate Secret Modal ───────────────── */

function ConfirmRotateSecretModal({ open, onClose, onConfirm, isLoading }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-md bg-[#0a1224] border border-sky-900/40 p-5 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-amber-300/80">
              Confirm
            </div>
            <h3 className="mt-1 text-[15px] font-semibold tracking-tight text-neutral-100">
              Rotate signing secret?
            </h3>
          </div>
          <IconBtn title="Close" onClick={onClose}>
            <X className="h-4 w-4" />
          </IconBtn>
        </div>

        <div className="text-[12.5px] text-neutral-300 space-y-3 leading-relaxed">
          <p>
            Rotating your webhook secret will <span className="text-neutral-100 font-medium">immediately change</span>{" "}
            the signature used for all future deliveries.
          </p>
          <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-[12px] text-amber-200/90">
            This may <span className="text-amber-100 font-medium">interrupt existing automations</span> until you update
            the secret in your downstream systems (env vars, workflow tools, reverse proxies).
          </div>
          <p className="text-neutral-400">Make sure you've updated your configurations before proceeding.</p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-sky-900/30">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-md border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25 disabled:opacity-50 disabled:hover:bg-amber-500/15"
          >
            {isLoading && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
            Rotate secret
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Helpers ───────────────── */

function planAllowsWebhooks(org) {
  const plan = (org?.plan || org?.subscription || "").toString().toLowerCase();
  return plan.includes("tifirm");
}

/* ───────────────── Page ───────────────── */

export default function Webhooks() {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // subscription gate
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState(null);
  const [canUseWebhooks, setCanUseWebhooks] = useState(false);

  // configuration state from backend
  const [loading, setLoading] = useState(true);
  const [endpointUrl, setEndpointUrl] = useState("");
  const [secretToken, setSecretToken] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [isActive, setIsActive] = useState(false);

  // ui state
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [rotateModalOpen, setRotateModalOpen] = useState(false);

  // simulate
  const EVENT_TYPES = useMemo(
    () => [
      { id: "consumer", label: "Consumer" },
      { id: "corporate", label: "Corporate" },
      { id: "email", label: "Email" },
      { id: "ip", label: "IP" },
      { id: "hostname", label: "Hostname" },
    ],
    []
  );
  const [simType, setSimType] = useState("corporate");
  const [simSending, setSimSending] = useState(false);
  const [simResult, setSimResult] = useState(null);
  // Simulation outcome lives next to the simulate card, not in the global
  // alert strip — the result is card-scoped, the rest of the page isn't
  // affected by whether a test event went out.
  const [simBanner, setSimBanner] = useState(null); // { kind: "success"|"error", text }

  const secretMasked = useMemo(() => {
    if (!secretToken) return "—";
    if (showSecret) return secretToken;
    const last4 = secretToken.slice(-4);
    return `••••-••••-••••-${last4}`;
  }, [secretToken, showSecret]);

  /* ── 1) Load subscription, gate the page ── */
  useEffect(() => {
    (async () => {
      try {
        setSubLoading(true);
        setSubError(null);

        const res = await fetch(LICENSE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        const org = data?.organization || null;
        const allowed = planAllowsWebhooks(org);
        setCanUseWebhooks(allowed);
      } catch (e) {
        console.error(e);
        setSubError("Unable to verify your subscription.");
        setCanUseWebhooks(false);
      } finally {
        setSubLoading(false);
      }
    })();
  }, []);

  /* ── 2) After allowed → load webhook config ── */
  useEffect(() => {
    if (!canUseWebhooks) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setApiError(null);
        setSuccess(null);

        const res = await fetch(HANDLER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ action: "get" }),
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load webhook settings.");

        setEndpointUrl(data?.endpoint_url || "");
        setSecretToken(data?.secret_token || "");
        setCreatedAt(data?.created_at || null);
        setIsActive(Boolean(Number(data?.is_active || 0)));
      } catch (e) {
        console.error(e);
        setApiError(e.message || "Failed to load webhook configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, [canUseWebhooks]);

  const handleSaveConfig = useCallback(
    async ({ endpoint }) => {
      try {
        setSaving(true);
        setApiError(null);
        setSuccess(null);

        const res = await fetch(HANDLER_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ action: "create", endpoint_url: endpoint }),
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        if (!res.ok || data?.success !== true) throw new Error(data?.error || "Save failed");

        setEndpointUrl(endpoint || "");
        if (data?.secret) setSecretToken(data.secret);
        setSuccess("Webhook configuration saved.");
        setModalOpen(false);
      } catch (e) {
        console.error(e);
        setApiError(e.message || "Failed to save configuration.");
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const handleRotateSecret = useCallback(async () => {
    try {
      setRotating(true);
      setApiError(null);
      setSuccess(null);

      const res = await fetch(HANDLER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ action: "regenerate" }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const data = await res.json();
      if (!res.ok || data?.success !== true || !data?.new_secret) {
        throw new Error(data?.error || "Rotate failed");
      }
      setSecretToken(data.new_secret);
      setSuccess("Signing secret rotated successfully.");
    } catch (e) {
      console.error(e);
      setApiError(e.message || "Failed to rotate secret.");
    } finally {
      setRotating(false);
      setRotateModalOpen(false);
    }
  }, []);

  const handleToggleActive = useCallback(async () => {
    try {
      setToggling(true);
      setApiError(null);
      setSuccess(null);

      const action = isActive ? "deactivate" : "activate";
      const res = await fetch(HANDLER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ action }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const data = await res.json();
      if (!res.ok || data?.success !== true) throw new Error(data?.error || `Failed to ${action}`);
      setIsActive(!isActive);
      setSuccess(`Webhook ${action === "activate" ? "enabled" : "disabled"}.`);
    } catch (e) {
      console.error(e);
      setApiError(e.message || "Failed to update status.");
    } finally {
      setToggling(false);
    }
  }, [isActive]);

  const handleSimulate = useCallback(async () => {
    try {
      setSimSending(true);
      setSimBanner(null);
      setSimResult(null);

      const started = performance.now();
      const res = await fetch(HANDLER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ action: "test", type: simType }),
      });

      const ms = Math.max(1, Math.round(performance.now() - started));
      let text = "";
      try { text = await res.text(); } catch {}
      setSimResult({ status: res.status, ms, body: text || "(no body)" });

      if (!res.ok) {
        try {
          const data = JSON.parse(text);
          throw new Error(data?.error || `Simulation failed with status ${res.status}.`);
        } catch {
          throw new Error(`Simulation failed with status ${res.status}.`);
        }
      }
      setSimBanner({ kind: "success", text: "Webhook simulated successfully." });
    } catch (e) {
      console.error(e);
      setSimBanner({ kind: "error", text: e.message || "Simulation failed." });
    } finally {
      setSimSending(false);
    }
  }, [simType]);

  const handleUpgrade = () => {
    try { window.location.href = "/upgrades"; } catch {}
  };

  const isGated = !subLoading && !canUseWebhooks;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header — matches the rest of the dashboard (Threat Feeds / PaymentFraud). */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Webhooks</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        <main className="relative flex-1 px-4 md:px-6 pb-12">
          {/* Tier gate — same calm CTA pattern as PaymentFraud / BrandProtection */}
          {isGated && (
            <div className="max-w-2xl mx-auto mt-12">
              <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)] p-8 text-center">
                <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
                  <Webhook className="h-6 w-6 text-sky-300" />
                </div>
                <h2 className="text-xl font-semibold text-white">
                  Webhooks are an upgraded feature
                </h2>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
                  Real-time event delivery and integration streaming are available on the{" "}
                  <span className="text-sky-200 font-medium">Threat Intelligence</span> plan.
                </p>
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleUpgrade}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
                  >
                    View upgrade options
                  </button>
                  <button
                    type="button"
                    onClick={() => { window.location.href = "/"; }}
                    className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
                  >
                    Back to dashboard
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Global alerts */}
          {!isGated && (
            <div className="mt-4 space-y-3">
              {success && (
                <AlertBanner kind="success" onClose={() => setSuccess(null)}>
                  {success}
                </AlertBanner>
              )}
              {(apiError || subError) && (
                <AlertBanner
                  kind="error"
                  onClose={() => (subError ? setSubError(null) : setApiError(null))}
                >
                  {subError || apiError}
                </AlertBanner>
              )}
            </div>
          )}

          {/* Content grid */}
          {!isGated && (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Card 1: Webhook Configuration */}
              <SurfaceCard noPad>
                <CardHeader
                  icon={Link2}
                  title="Webhook configuration"
                  right={
                    <span
                      className={`inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset ${
                        isActive
                          ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30"
                          : "bg-neutral-500/10 text-neutral-400 ring-neutral-500/30"
                      }`}
                    >
                      {isActive ? "Enabled" : "Disabled"}
                    </span>
                  }
                />
                <div className="p-5 space-y-5">
                  {subLoading || loading ? (
                    <ConfigSkeleton />
                  ) : (
                    <>
                      <Section title="Endpoint">
                        <dl className="grid grid-cols-1 gap-y-3 text-[12.5px]">
                          <Field
                            label="URL"
                            value={
                              endpointUrl
                                ? <CopyInline copyText={endpointUrl}>{endpointUrl}</CopyInline>
                                : <span className="text-neutral-500 italic">Not configured</span>
                            }
                          />
                          <Field
                            label="Created"
                            value={createdAt
                              ? <span className="font-mono tabular-nums">{createdAt}</span>
                              : "—"}
                          />
                        </dl>
                      </Section>

                      <Section title="Signing secret">
                        <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 px-3 py-2 flex items-center justify-between gap-3">
                          <div className="inline-flex items-center gap-2 min-w-0">
                            <Shield className="h-3.5 w-3.5 text-sky-300/80 shrink-0" />
                            <span className="font-mono text-[12.5px] truncate text-neutral-100">
                              {secretMasked}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <IconBtn
                              title={showSecret ? "Hide" : "Show"}
                              onClick={() => setShowSecret((v) => !v)}
                              disabled={!secretToken}
                            >
                              {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </IconBtn>
                            {secretToken && (
                              <button
                                type="button"
                                onClick={() => { setSuccess(null); setApiError(null); setRotateModalOpen(true); }}
                                disabled={rotating}
                                className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
                                title="Rotate signing secret"
                              >
                                <RefreshCw className={`h-3 w-3 ${rotating ? "animate-spin" : ""}`} />
                                Rotate
                              </button>
                            )}
                          </div>
                        </div>
                      </Section>

                      {/* Footer actions */}
                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-sky-900/30">
                        <button
                          type="button"
                          onClick={handleToggleActive}
                          disabled={!endpointUrl || toggling}
                          className="text-[12.5px] font-medium px-3 py-1.5 rounded-md border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-50"
                        >
                          {toggling ? "Updating…" : isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSuccess(null); setApiError(null); setModalOpen(true); }}
                          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25"
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                          Configure
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </SurfaceCard>

              {/* Card 2: Simulate */}
              <SurfaceCard noPad>
                <CardHeader
                  icon={Send}
                  title="Simulate webhook"
                  right={<span className="text-[11px] text-neutral-500">Signed test event</span>}
                />
                <div className="p-5 space-y-4">
                  {/* Card-scoped banner — simulation success / failure is a
                      property of THIS card, not the page. */}
                  {simBanner && (
                    <AlertBanner kind={simBanner.kind} onClose={() => setSimBanner(null)}>
                      {simBanner.text}
                    </AlertBanner>
                  )}

                  <Section title="Event type">
                    <div className="flex flex-wrap gap-1.5">
                      {EVENT_TYPES.map((opt) => {
                        const active = simType === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setSimType(opt.id)}
                            className={`inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded border transition-colors ${
                              active
                                ? "bg-sky-500/15 border-sky-500/40 text-sky-100"
                                : "bg-transparent border-sky-900/40 text-neutral-400 hover:border-sky-700/50 hover:text-neutral-200"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-[11px] text-neutral-500">
                      The payload is generated on the server and signed with your secret.
                    </div>
                  </Section>

                  <div className="flex items-center justify-end pt-3 border-t border-sky-900/30">
                    <button
                      type="button"
                      onClick={handleSimulate}
                      disabled={simSending || !isActive || !endpointUrl}
                      title={
                        !endpointUrl
                          ? "Configure an endpoint first"
                          : !isActive
                          ? "Activate the webhook first"
                          : "Send a signed test event"
                      }
                      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 disabled:opacity-50 disabled:hover:bg-sky-500/15"
                    >
                      {simSending ? (
                        <>
                          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5" />
                          Send test
                        </>
                      )}
                    </button>
                  </div>

                  {simResult && (
                    <Section title="Last response">
                      <div className="rounded-md border border-sky-900/30 bg-[#0b1426]/60 overflow-hidden">
                        <div className="px-3 py-2 border-b border-sky-900/30 flex items-center justify-between text-[11px]">
                          <span className="text-neutral-400">
                            Status:{" "}
                            <span
                              className={`font-mono font-semibold ${
                                simResult.status >= 200 && simResult.status < 300
                                  ? "text-emerald-300"
                                  : "text-red-300"
                              }`}
                            >
                              {simResult.status}
                            </span>
                          </span>
                          <span className="text-neutral-400">
                            Time:{" "}
                            <span className="font-mono tabular-nums text-neutral-200">
                              {simResult.ms} ms
                            </span>
                          </span>
                        </div>
                        <pre className="px-3 py-2 text-[11.5px] whitespace-pre-wrap break-words text-neutral-300 font-mono leading-relaxed max-h-48 overflow-auto">
{simResult.body}
                        </pre>
                      </div>
                    </Section>
                  )}
                </div>
              </SurfaceCard>
            </div>
          )}
        </main>
      </div>

      <ConfigureWebhookModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialEndpoint={endpointUrl}
        onSave={handleSaveConfig}
        saving={saving}
      />

      <ConfirmRotateSecretModal
        open={rotateModalOpen}
        onClose={() => setRotateModalOpen(false)}
        isLoading={rotating}
        onConfirm={handleRotateSecret}
      />
    </div>
  );
}

/* Loading skeleton inside the configuration card — keeps the section
   structure (Endpoint / Created / Secret) visible while we wait, so the
   card height doesn't jump when the response lands. */
function ConfigSkeleton() {
  return (
    <div className="space-y-5">
      <div>
        <SkeletonBar className="h-2.5 w-24 mb-3" />
        <div className="space-y-3">
          <div>
            <SkeletonBar className="h-2 w-16 mb-1.5" />
            <SkeletonBar className="h-3 w-2/3" />
          </div>
          <div>
            <SkeletonBar className="h-2 w-16 mb-1.5" />
            <SkeletonBar className="h-3 w-1/3" />
          </div>
        </div>
      </div>
      <div>
        <SkeletonBar className="h-2.5 w-28 mb-3" />
        <SkeletonBar className="h-9 w-full rounded-md" />
      </div>
      <div className="pt-3 border-t border-sky-900/30 flex justify-end gap-2">
        <SkeletonBar className="h-7 w-20 rounded-md" />
        <SkeletonBar className="h-7 w-24 rounded-md" />
      </div>
    </div>
  );
}
